/**
 * The parts the rate-reduction and payment-reduction landing pages share.
 *
 * Both pages are the same page with a different headline: greet the member, state the
 * one number they were mailed about, ask for the application, promise the reward, offer
 * the way out, and close with the lifetime saving. Only the headline differs, so only the
 * headline lives in the page files.
 *
 * Every figure below is GUARDED. A refinance offer is a prediction made when the
 * prescreen batch ran, and by the time the member opens the link the rate can have moved,
 * the loan can have been repriced and the payoff can have changed. The rule these
 * components follow is the one in `landing.test.jsx`: show a number only when it is known
 * AND it is an improvement, and otherwise say nothing rather than something hopeful.
 */
import {formatMoney} from 'accounting';
import classNames from 'classnames';
import {Fragment} from 'react';

import Button from '-/components/general/Button';
import BypassAuthButton from '-/components/fields/BypassAuthButton';
import {forgetOffer} from '-/util/util';

/**
 * The reward is a campaign promise rather than anything on the offer: members can raise
 * their gift card by playing the reward game after the loan funds. The ceiling is a
 * marketing constant, so it is named here rather than being loose in the copy.
 */
const MAX_ADDITIONAL_REWARD = 1000;

/** Below this the copy stops quoting a figure -- see `Default` and `Compact`, same floor. */
const LIFETIME_SAVINGS_FLOOR = 1000;

export const CTA_TEXT = 'Complete Your Application';

/**
 * What the button says to someone who has already started.
 *
 * Word for word what `Default` says (Default.js:49), because a member can be moved
 * between arms between visits -- they were mailed one page and a `?t=` or a re-drop can
 * land them on another -- and the invitation to come back should not change under them.
 */
export const CTA_TEXT_RETURNING = 'Let\'s finish where you left off!';

/**
 * The new rate, but only when it is genuinely lower than what the member has today.
 *
 * `Default` gets this wrong -- it gates on the truthiness of `newRate` alone, so an offer
 * whose rate went UP is announced as one the member "locked down" (pinned in
 * `landing.test.jsx`). These pages must not repeat it: a rate-reduction page showing a
 * rate increase is the worst version of that bug, because the headline is the rate.
 */
export const improvedRate = (dealInfo = {}) => {
    const {existingRate, newRate} = dealInfo;

    if (!(newRate > 0) || !(existingRate > 0) || newRate >= existingRate) {
        return null;
    }

    return newRate;
};

/**
 * The monthly saving the payment page advertises.
 *
 * A MARKETING CEILING, not this member's number -- the same construct as
 * `MAX_ADDITIONAL_REWARD` above, and the figure in the approved comps. The copy reads
 * "up to", so it is a bound on the offer rather than a promise about the reader.
 *
 * It is static because a per-member figure cannot carry this headline. Our refi model
 * holds the term at the member's remaining term and moves only the rate
 * (dod-utils `calcInitialDealInfo` sets the term, `getDealTerms` buckets it), so the real
 * reduction tracks the rate drop alone: about $10/month per point on a $20,000 payoff.
 * Reaching $109 needs roughly seven points off $30,000. Set in 68px type, the honest
 * per-member number would read as a joke for most of the file.
 *
 * If this is ever substantiated differently, or a genuine payment-reduction deal (a
 * longer term) gets calculated and stored, this is the one place to change.
 */
export const ADVERTISED_PAYMENT_REDUCTION = 109;

/**
 * Whether this member's payment would positively go UP under the offer.
 *
 * The gate on the headline. "Up to" covers a member who saves less than the advertised
 * ceiling; it does NOT cover one whose payment would rise, because for them "you could
 * lower your monthly payment" is false at any figure. `rateIncrease` in the fixtures is a
 * real offer where the payment went from $430.18 to $465.02.
 *
 * Returns false when we cannot say -- an unpriced offer has no new payment to compare, and
 * withholding a whole marketing claim on the strength of a missing field would silence the
 * page for offers that are perfectly fine. Only positive evidence suppresses it.
 */
export const paymentWouldRise = (dealInfo = {}) => {
    const {currMonthlyPayment, monthlyPayment} = dealInfo;

    if (!(currMonthlyPayment > 0) || !(monthlyPayment > 0)) {
        return false;
    }

    return monthlyPayment >= currMonthlyPayment;
};

/**
 * "Congratulations, Dennis!" -- but only when there is something to congratulate, and
 * only when the member is not already halfway through.
 *
 * Three states, in this order:
 *
 *   returning     they left an application part-finished, so the page welcomes them back
 *                 rather than congratulating them a second time. Beats `hasGoodNews`:
 *                 "let's get started" is plainly wrong for someone already started.
 *   hasGoodNews   the page's own headline figure is showing, so there is something to
 *                 celebrate.
 *   neither       congratulating a member whose rate went up is the kind of false note
 *                 these pages exist to avoid, so the greeting drops to an invitation.
 *
 * The name is optional. Three of the older variants interpolate it unguarded and greet a
 * member with the literal word "undefined" when the offer came from somewhere that did
 * not supply one; these pages leave it out instead.
 */
export const greetingFor = (name, opts = {}) => {
    const {hasGoodNews = false, returning = false} = opts;

    let opener = 'Let\'s get started';

    if (returning) {
        opener = 'Welcome back';
    } else if (hasGoodNews) {
        opener = 'Congratulations';
    }

    return name ? `${opener}, ${name}!` : `${opener}!`;
};

/**
 * The reward paragraph, or nothing when this offer carries no gift card.
 *
 * Gated on the offer's own `giftAmount` rather than on the $50 in the approved copy, so
 * an offer without a gift never promises one.
 */
export const Reward = props => {
    const {classes = {}, giftAmount} = props;

    if (!(giftAmount > 0)) {
        return null;
    }

    return (
        <Fragment>
            <p className={classes.reward}>
                You&apos;ve also qualified for a{' '}
                <span className={classes.bold}>
                    {formatMoney(giftAmount, {precision: 0})} Giftogram<sup>&reg;</sup> Gift Card
                </span>
                {' '}when your loan funds, <span className={classes.bold}>plus</span> you&apos;ll
                have the opportunity to
            </p>
            {/* Broken out onto its own line so the bonus reward is the part that lands. */}
            <p className={classes.rewardAccentLine}>
                add up to {formatMoney(MAX_ADDITIONAL_REWARD, {precision: 0})} more to your reward.*
            </p>
        </Fragment>
    );
};

/** The thin accent rule the comps put above the reward block and below "This is not me". */
export const Rule = props => {
    const {classes = {}} = props;

    return <hr className={classes.rule} />;
};

/**
 * "This is not me", styled for these pages.
 *
 * The shared `NotMe` pins its link to black and lower-cases the label; the comps want
 * the creditor's own colour and a capital. The ACTION is the shared one -- see
 * `forgetOffer` -- so only the presentation differs.
 */
export const ThisIsNotMe = props => {
    const {classes = {}} = props;
    const onClick = e => {
        e.preventDefault();

        forgetOffer();
    };

    return (
        <p className={classNames(classes.p, classes.notMe)}>
            <a className={classes.notMeLink} href="#" onClick={onClick}>
                This is not me
            </a>
        </p>
    );
};

/** The closing line. Silent unless the saving is known, positive and worth quoting. */
export const LifetimeSavings = props => {
    const {classes = {}, lifeTimeSavings} = props;

    if (!(lifeTimeSavings >= LIFETIME_SAVINGS_FLOOR)) {
        return null;
    }

    return (
        <p className={classes.p}>
            You are one step closer to interest savings of about{' '}
            <span className={classes.bold}>{formatMoney(lifeTimeSavings)}</span>.
        </p>
    );
};

/**
 * The call to action.
 *
 * `BypassAuthButton` replaces it when the URL already carries an authorisation, which is
 * how a CCR opens a member's application without re-validating them -- the same swap
 * every other landing page makes.
 */
export const CallToAction = props => {
    const {auth = {}, classes = {}, currentStepLastSession, onNextClick} = props;
    const {authInfo, authType, showBypassAuthButton} = auth;
    // The step the member was on when they last left, so truthy means they started.
    const label = currentStepLastSession ? CTA_TEXT_RETURNING : CTA_TEXT;

    if (showBypassAuthButton) {
        return (
            <BypassAuthButton
                authType={authType}
                authInfo={authInfo}
                buttonText={label} />
        );
    }

    return (
        <Button
            className={classes.button}
            width={90}
            tag="a"
            href="#"
            onClick={onNextClick}
            title={label}
            role="button">
            {label}
            <span className={classes.buttonArrow} aria-hidden="true">&rarr;</span>
        </Button>
    );
};
